import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getUserDoc, setUserDoc, getUserProfile, setUserProfile, onFirebaseAuthStateChanged, firebaseSignOut, refreshEmailVerification } from '../services/firebase';
import { createStripeCustomer, initializeWallet, checkCredits, getWallet } from '../services/api';

const AuthContext = createContext(null);

const toSessionUser = (firebaseUser) => {
  const { uid, email, displayName: name, photoURL: picture, emailVerified } = firebaseUser;
  return { uid, email, name, picture, emailVerified: Boolean(emailVerified) };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [pendingSearch, setPendingSearch] = useState(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const activeUidRef = useRef(null);
  const hydrationRef = useRef(null);

  // ── Session restore and background account hydration ────────────────────────

  /**
   * Fetches account and billing details after the session is already visible.
   * Wallet/Stripe latency must not make a real Firebase session look signed out.
   */
  const hydrateAccount = useCallback(async (firebaseUser) => {
    const { uid, email, displayName: name } = firebaseUser;
    setIsLoadingProfile(true);
    try {
      const userSnap = await getUserDoc(uid);

      let balance = 0;

      if (!userSnap.exists()) {
        // New user — Firestore doc + Stripe customer (backend handles wallet init internally)
        await setUserDoc(uid, { email, name, phone: '', tracking: 0 });
        let newStripeCustomerId = '';
        try {
          const res = await createStripeCustomer(uid, email, name);
          newStripeCustomerId = res.id || res.stripeCustomerId || '';
          if (activeUidRef.current === uid) setStripeCustomerId(newStripeCustomerId);
        } catch (err) {
          console.error('Stripe customer creation failed:', err);
        }
        await setUserProfile(uid, {
          userId: uid,
          stripeCustomerId: newStripeCustomerId,
          creditBalance: 0,
          totalCreditsPurchased: 0,
          totalCreditsUsed: 0,
        });
        // Wallet was created by createStripeCustomer — just fetch the balance
        try {
          const walletRes = await getWallet(uid);
          balance = walletRes.wallet?.sea ?? 0;
          if (activeUidRef.current === uid && walletRes.wallet?.stripeCustomerId) {
            setStripeCustomerId(walletRes.wallet.stripeCustomerId);
          }
        } catch (err) {
          console.error('Wallet fetch failed for new user:', err);
        }
      } else {
        // Existing user — ensure wallet exists, then fetch balance + stripeCustomerId
        try {
          await initializeWallet(uid); // idempotent: no-op if already initialized
          const walletRes = await getWallet(uid);
          balance = walletRes.wallet?.sea ?? 0;
          if (activeUidRef.current === uid && walletRes.wallet?.stripeCustomerId) {
            setStripeCustomerId(walletRes.wallet.stripeCustomerId);
          }
        } catch (err) {
          console.error('Wallet init/fetch failed:', err);
          // Fall back to Firestore balance
          const profileSnap = await getUserProfile(uid);
          balance = profileSnap.exists() ? (profileSnap.data().creditBalance ?? 0) : 0;
          if (activeUidRef.current === uid && profileSnap.exists()) {
            setStripeCustomerId(profileSnap.data().stripeCustomerId || '');
          }
        }
      }

      if (activeUidRef.current === uid) setCreditBalance(balance);
    } catch (err) {
      // The user is already signed in. A failed profile or wallet request should
      // affect only that secondary information, never the session itself.
      console.error('Account hydration error:', err);
    } finally {
      if (activeUidRef.current === uid) setIsLoadingProfile(false);
    }
  }, []);

  /**
   * Firebase keeps the session in its browser persistence layer. Set this UI
   * state before any network-dependent profile work, then hydrate only once
   * per active user to avoid duplicate calls from login plus the auth observer.
   */
  const establishSession = useCallback((firebaseUser) => {
    const sessionUser = toSessionUser(firebaseUser);
    activeUidRef.current = sessionUser.uid;
    setUser(sessionUser);
    setIsEmailVerified(sessionUser.emailVerified);
    setIsAuthInitialized(true);

    if (hydrationRef.current?.uid !== sessionUser.uid) {
      const promise = hydrateAccount(firebaseUser);
      hydrationRef.current = { uid: sessionUser.uid, promise };
    }
  }, [hydrateAccount]);

  // ── Login ──────────────────────────────────────────────────────────────────

  const login = useCallback(async (firebaseUser) => {
    establishSession(firebaseUser);
    setIsAuthModalOpen(false);
    if (pendingSearch) {
      const cb = pendingSearch.fn;
      setPendingSearch(null);
      setTimeout(cb, 350);
    }
  }, [establishSession, pendingSearch]);

  const logout = useCallback(async () => {
    try {
      await firebaseSignOut();
    } catch (err) {
      console.error('Firebase sign out failed:', err);
    }
    activeUidRef.current = null;
    hydrationRef.current = null;
    setUser(null);
    setIsEmailVerified(false);
    setCreditBalance(0);
    setStripeCustomerId('');
    setIsAuthModalOpen(false);
    setIsPaymentModalOpen(false);
    setPendingSearch(null);
  }, []);

  // ── Restore session on page load ────────────────────────────────────────────

  useEffect(() => {
    const unsubscribe = onFirebaseAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        establishSession(firebaseUser);
      } else {
        activeUidRef.current = null;
        hydrationRef.current = null;
        setUser(null);
        setIsEmailVerified(false);
        setIsLoadingProfile(false);
        setIsAuthInitialized(true);
      }
    });
    return unsubscribe;
  }, [establishSession]);

  // ── Credit access ──────────────────────────────────────────────────────────

  const requestTrackingAccess = useCallback(async (onGranted) => {
    if (!user) {
      setPendingSearch({ fn: onGranted });
      setIsAuthModalOpen(true);
      return;
    }
    // The API rejects unverified accounts, so ask for verification here rather
    // than spending a credit check and surfacing a 403 as a tracking failure.
    if (!isEmailVerified) {
      setPendingSearch({ fn: onGranted });
      setIsAuthModalOpen(true);
      return;
    }
    const email = user.email?.toLowerCase() ?? '';
    if (email.endsWith('@ridelink.com') || email.endsWith('@ride-link.com')) {
      onGranted();
      return;
    }
    try {
      const { sufficient } = await checkCredits(user.uid);
      if (sufficient) {
        onGranted();
      } else {
        setPendingSearch({ fn: onGranted });
        setIsPaymentModalOpen(true);
      }
    } catch (err) {
      console.error('Credit check failed:', err);
      // Fallback: let the consume call handle insufficient credits
      onGranted();
    }
  }, [user, isEmailVerified]);

  /**
   * Re-checks verification with Firebase, for when the user clicks the link in
   * another tab and comes back. Resumes a pending search once verified.
   */
  const recheckEmailVerification = useCallback(async () => {
    const verified = await refreshEmailVerification();
    setIsEmailVerified(verified);
    if (verified) {
      setUser((current) => (current ? { ...current, emailVerified: true } : current));
      setIsAuthModalOpen(false);
      if (pendingSearch) {
        const cb = pendingSearch.fn;
        setPendingSearch(null);
        setTimeout(cb, 350);
      }
    }
    return verified;
  }, [pendingSearch]);

  const updateCreditBalance = useCallback((newBalance) => {
    setCreditBalance(newBalance);
  }, []);

  // ── Payment ────────────────────────────────────────────────────────────────

  const markPaid = useCallback((newBalance) => {
    setCreditBalance(newBalance);
    setIsPaymentModalOpen(false);
    if (pendingSearch) {
      const cb = pendingSearch.fn;
      setPendingSearch(null);
      setTimeout(cb, 350);
    }
  }, [pendingSearch]);

  const openPaymentModal = useCallback(() => setIsPaymentModalOpen(true), []);

  const openAuthModal = useCallback(() => setIsAuthModalOpen(true), []);

  const updateStripeCustomerId = useCallback((id) => setStripeCustomerId(id), []);

  // ── Modal controls ─────────────────────────────────────────────────────────

  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
    setPendingSearch(null);
  }, []);

  const closePaymentModal = useCallback(() => {
    setIsPaymentModalOpen(false);
    setPendingSearch(null);
  }, []);

  const isWhitelistedUser = user
    ? (user.email?.toLowerCase().endsWith('@ridelink.com') || user.email?.toLowerCase().endsWith('@ride-link.com'))
    : false;

  return (
    <AuthContext.Provider value={{
      user,
      creditBalance,
      stripeCustomerId,
      isLoadingProfile,
      isAuthInitialized,
      isAuthModalOpen,
      isPaymentModalOpen,
      isWhitelistedUser,
      isEmailVerified,
      login,
      recheckEmailVerification,
      logout,
      requestTrackingAccess,
      updateCreditBalance,
      markPaid,
      openPaymentModal,
      openAuthModal,
      updateStripeCustomerId,
      closeAuthModal,
      closePaymentModal,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
